import {
  AlertDialog,
  BodyText,
  Button,
  Callout,
  Label,
  toast,
} from "@/components/ui";
import { RequestGroup, type ServiceRequest } from "@curai/js-fhir-tools";
import { useParams } from "@tanstack/react-router";
import { useState } from "react";
import { useCancelOrder } from "../network/useCancelOrder";
import type { LabOrderEntryActionSummary } from "../network/useLabOrdersByEncounter";
import { useSubmitOrder } from "../network/useSubmitOrder";

export const SubmittedOrder = ({
  encounterId,
  requestGroup,
  serviceRequests,
  actionSummary,
}: {
  encounterId: string;
  requestGroup: RequestGroup;
  serviceRequests: ServiceRequest[];
  actionSummary: LabOrderEntryActionSummary;
}) => {
  const { patientId } = useParams({ from: "/patient/$patientId" });
  const [showCancelOrderPrompt, setShowCancelOrderPrompt] = useState(false);
  const { mutateAsync: resubmitOrder, isPending: isResubmittingOrder } =
    useSubmitOrder({
      patientId,
      encounterId,
      requestGroupId: requestGroup.id as string,
      markOnHold: false,
    });

  const { mutateAsync: cancelOrder, isPending: isCancellingOrder } =
    useCancelOrder({
      patientId,
      encounterId,
      requestGroupId: requestGroup.id as string,
    });

  const handleCancelOrder = async () => {
    try {
      await cancelOrder();
      setShowCancelOrderPrompt(false);
    } catch {
      toast.error("An error occurred cancelling the order.");
    }
  };

  const handleResubmitOrder = async () => {
    try {
      await resubmitOrder();
    } catch {
      toast.error("An error occurred resubmitting the order.");
    }
  };

  return (
    <>
      <div className="grid gap-2 grid-cols-2">
        {requestGroup.orderStatus === RequestGroup.OrderStatus.FAILED && (
          <div className="col-span-2">
            <Callout
              title="Submission Failed"
              variant="danger"
              className="flex flex-col gap-2"
            >
              <BodyText size="sm">
                {`This order failed to send to the lab. ${
                  actionSummary.readyForSubmission
                    ? "If resending the lab fails, the order will need to be cancelled and re-ordered."
                    : "The order will need to be cancelled and re-ordered."
                }`}
              </BodyText>

              <div className="flex gap-2">
                {actionSummary.canCancel && (
                  <Button
                    size="xs"
                    className="w-fit"
                    variant="danger"
                    onClick={() => setShowCancelOrderPrompt(true)}
                    isLoading={isCancellingOrder}
                    disabled={isResubmittingOrder}
                  >
                    Cancel Order
                  </Button>
                )}
                {actionSummary.readyForSubmission && (
                  <Button
                    size="xs"
                    className="w-fit"
                    variant="primary"
                    onClick={handleResubmitOrder}
                    isLoading={isResubmittingOrder}
                    disabled={isCancellingOrder}
                  >
                    Resend to Lab
                  </Button>
                )}
              </div>
            </Callout>
          </div>
        )}
        <div className="grid">
          <Label>Lab Network</Label>
          <BodyText size="sm">{requestGroup.labNetworkLabel}</BodyText>
        </div>
        <div className="grid">
          <Label>Billing Method</Label>
          <BodyText size="sm">{requestGroup.billingMethodLabel}</BodyText>
        </div>
        <div className="grid">
          <Label>Order Status</Label>
          <BodyText
            size="sm"
            textColor={
              requestGroup.orderStatus === RequestGroup.OrderStatus.FAILED
                ? "error"
                : "primary"
            }
          >
            {requestGroup.orderStatusLabel || requestGroup.statusLabel}
          </BodyText>
        </div>
        {requestGroup.orderComment && (
          <div className="grid">
            <Label>Order Comment</Label>
            <BodyText size="sm">{requestGroup.orderComment}</BodyText>
          </div>
        )}
        {requestGroup.icd10Codes.length > 0 && (
          <div className="col-span-2 grid">
            <Label>ICD10 Codes</Label>
            <BodyText size="sm">
              <ul className="list-outside list-disc pl-5">
                {requestGroup.icd10Codes.map((code) => (
                  <li key={`${code.id}-${code.text}`}>
                    {`${code.coding?.[0]?.code} - ${code.text}`}
                  </li>
                ))}
              </ul>
            </BodyText>
          </div>
        )}
        <div className="col-span-2 grid">
          <Label>Tests Ordered</Label>
          <BodyText size="sm">
            <ul className="list-outside list-disc pl-5">
              {serviceRequests.map((serviceRequest) => (
                <li key={serviceRequest.id}>{serviceRequest.testName}</li>
              ))}
            </ul>
          </BodyText>
        </div>
      </div>
      <AlertDialog
        isOpen={showCancelOrderPrompt}
        onOpenChange={setShowCancelOrderPrompt}
        isControlled
        title="Warning: Order Already Submitted"
        body="This order has already been submitted to the lab. Cancelling this order will remove it from the patient's chart, but will not inform the lab of the cancellation."
        actionLabel="Cancel Order"
        cancelLabel="Go Back"
        onAction={handleCancelOrder}
        onCancel={() => setShowCancelOrderPrompt(false)}
        // TODO: Remove this once we fix the AlertDialogProvider
        // biome-ignore lint/complexity/noUselessFragments: We need to pass a trigger element to the AlertDialog so the AlertDialogProvider doesn't render the default trigger element
        triggerElement={<></>}
      />
    </>
  );
};
